import React from "react";
import { useTranslation } from "react-i18next";
import { rand } from "../../../common/helpers/rand.js";
import WizardLogotype from "./WizardLogotype.jsx";

const { DESIGNING_LAYOUT } = Config;

const HEADER_TIME = {
    min: DESIGNING_LAYOUT?.minTime || 5000,
    max: DESIGNING_LAYOUT?.maxTime || 7000,
};

const SVG_WSTEP_START = 0;

export const ON_DESIGNING_END_TIMEOUT = 1000;

/**
 *
 * _onDesigningEnd() tells about done
 *
 * _onDesigningEnd() calls from DynamicHeaderText() > next() > _onDesigningEnd()
 */
export default function DesigningLayout(props) {
    const { t } = useTranslation();

    const HeaderList = [
        t("WizardDesigningLayoutHeader1"),
        t("WizardDesigningLayoutHeader2"),
        t("WizardDesigningLayoutHeader3"),
        t("WizardDesigningLayoutHeader4"),
        t("WizardDesigningLayoutHeader5"),
        t("WizardDesigningLayoutHeader6"),
    ];

    const { generatePromises } = props;
    const { onDesigningStart, onDesigningEnd } = props;
    const { dynamicMode, variables, completeSteps, setStepByIndex, step, show, logotype, designing } = props;

    const refRoot = React.useRef(null);

    const refWizardStepList = React.useRef(null);

    const [domSvgBlocks, setDomSvgBlocks] = React.useState(null);
    const refWizardStepListBlocks = React.useRef(null);

    const [domSvgNames, setDomSvgNames] = React.useState(null);
    const refWizardStepListNames = React.useRef(null);

    const refText = React.useRef(null);
    const refDots = React.useRef(null);

    const [AC, setAC] = React.useState(new AbortController());

    // Load & append SVG
    React.useEffect(() => {
        setTimeout(LoadSVG, 1000);

        return () => {
            AC.abort();
        };
    }, []);
    // SVG Steps click events
    React.useEffect(ModifyDomSteps, [domSvgBlocks, domSvgNames, variables]);
    // Start
    React.useEffect(OnShowChange, [show]);
    // SVG Steps
    React.useEffect(UpdateSVGView, [domSvgBlocks, domSvgNames, completeSteps, step, show]);
    // SVG Mask
    React.useEffect(UseRootOrMask, [domSvgBlocks, domSvgNames, step, show]);

    if (dynamicMode) {
        // Header text
        React.useEffect(DynamicHeaderText, [show, variables]);
        // Header dots
        React.useEffect(DynamicHeaderDots, [show]);
    }

    function LoadSVG() {
        Promise.all([
            fetch("/img/wizard-step-list.svg", { signal: AC.signal }).then(r => r.text()),
            fetch("/img/wizard-step-list-names.svg", { signal: AC.signal }).then(r => r.text()),
        ])
            .then(([blockXml, namesXml]) => {
                let svg;

                // Blocks
                svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
                svg.innerHTML = blockXml;
                svg = svg.firstChild;
                svg.classList.add("interactive");
                refWizardStepListBlocks.current.parentElement.appendChild(svg);

                // Sign .step-#
                {
                    let i = SVG_WSTEP_START;
                    for (const dom of svg.querySelectorAll(".step-")) {
                        dom.classList.remove("step-");
                        dom.classList.add(`step-${i}`);

                        i++;
                    }
                }

                setDomSvgBlocks(svg);

                // Names
                svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
                svg.innerHTML = namesXml;
                svg = svg.firstChild;
                svg.classList.add("interactive");
                svg.classList.add("names");
                refWizardStepListNames.current.parentElement.appendChild(svg);

                // Sign .step-#
                {
                    let i = SVG_WSTEP_START;
                    for (const dom of svg.querySelectorAll(".step-")) {
                        dom.classList.remove("step-");
                        dom.classList.add(`step-${i}`);

                        i++;
                    }
                }

                setDomSvgNames(svg);

                setTimeout(() => {
                    refWizardStepList.current.classList.add("loaded");
                }, 100);
            })
            .catch(e => {
                if (e.name !== "AbortError") {
                    throw new Error(e);
                }
            });
    }

    function ModifyDomSteps() {
        if (!domSvgBlocks || !domSvgNames) {
            return;
        }

        for (const domStep of domSvgBlocks.querySelectorAll("g.wsl-step")) {
            domStep.prevOnClick && domStep.removeEventListener("click", domStep.prevOnClick);
            domStep.addEventListener("click", onDomStepClick);

            domStep.prevOnClick = onDomStepClick;
        }

        for (const domStep of domSvgNames.querySelectorAll("text")) {
            domStep.prevOnClick && domStep.removeEventListener("click", domStep.prevOnClick);
            domStep.addEventListener("click", onDomStepClick);

            domStep.prevOnClick = onDomStepClick;

            {
                const tk = `WizardMobileMenu${domStep.dataset.text}`;
                const _t = t(tk);
                _t && (domStep.textContent = _t);
            }
        }
    }

    function OnShowChange() {
        const domWizardStepList = refWizardStepList?.current;
        const domRoot = refRoot?.current;
        const domWizardLogotype = domRoot.querySelector(".wizard-logotype");

        const isMobile = window.matchMedia("(max-width: 599.95px)").matches;

        if (show) {
            if (!isMobile && domWizardLogotype) {
                domWizardStepList.style.setProperty("max-width", `${domWizardLogotype.offsetWidth * 1.1}px`);
            }

            onDesigningStart(); // > WebsiteNew.jsx
        } else {
            if (!isMobile && domWizardLogotype) {
                domWizardStepList.style.removeProperty("width");
            }
        }
    }

    function UpdateSVGView() {
        if (!domSvgBlocks || !domSvgNames) {
            return;
        }

        domSvgBlocks.querySelector(".wsl-root").classList[show ? "add" : "remove"]("off");

        const domBlocksStepList = domSvgBlocks.querySelectorAll("g.wsl-step");
        const domNamesStepList = domSvgNames.querySelectorAll("text");

        const NamesByIndex = {};
        for (const domStep of domNamesStepList) {
            const stepNum = parseInt(domStep.className.baseVal.match(/step-(\d+)/, "$1")[1]);
            NamesByIndex[stepNum] = domStep;
        }

        const lastCompleteIndex = Object.entries(completeSteps)
            .reverse()
            .filter(([index, complete]) => complete)
            .map(([index]) => index)[0];

        for (const domStep of domBlocksStepList) {
            const stepIndex = parseInt(domStep.className.baseVal.match(/step-(\d+)/, "$1")[1]);

            domStep.classList.remove("off");

            const isComplete = completeSteps[stepIndex] && completeSteps[stepIndex]?.fullfilled;
            const isPrevComplete = completeSteps[stepIndex - 1] && completeSteps[stepIndex - 1]?.fullfilled;

            const domStepInactive = domStep.querySelector(".wsl-inactive");
            const domStepCurrent = domStep.querySelector(".wsl-current");
            const domStepActive = domStep.querySelector(".wsl-active");

            const domStepName = NamesByIndex[stepIndex];

            for (const g of domStep.querySelectorAll("g")) {
                g.classList.remove("show");
                g.classList.remove("off");
            }

            if (!domStepName) {
                console.warn(NamesByIndex, stepIndex);
                continue;
            }

            domStepName.classList.remove("current");

            if (step == stepIndex) {
                // Current Step
                domStepCurrent.classList.add("show");
                domStepName.classList.add("clickable");
                domStepName.classList.add("current");
            } else if (step < stepIndex) {
                // Prev Step
                if (!isComplete) {
                    domStepInactive.classList.add("show");
                } else {
                    domStepActive.classList.add("show");
                }

                if (
                    !isComplete &&
                    (lastCompleteIndex === undefined || stepIndex > lastCompleteIndex) &&
                    !isPrevComplete
                ) {
                    domStep.classList.add("off");
                    domStepName.classList.remove("clickable");
                } else {
                    domStepName.classList.add("clickable");
                }
            } else {
                // Next Step
                if (!isComplete) {
                    domStepInactive.classList.add("show");
                } else {
                    domStepActive.classList.add("show");
                }

                domStepName.classList.add("clickable");
            }
        }
    }

    function UseRootOrMask() {
        if (!domSvgBlocks || !domSvgNames) {
            return;
        }

        const domWslMask = domSvgBlocks.querySelector("#WslMask");
        const domRoot = domSvgBlocks.querySelector(".wsl-root");
        const domMaskify = domSvgBlocks.querySelector(".wsl-maskify");
        const domNames = domSvgNames.querySelector(".wsl-root-names");

        if (!show) {
            domWslMask.classList.remove("animate");
            domMaskify.classList.remove("animate");
            domRoot.classList.remove("hide");
            domMaskify.classList.add("hide");

            domNames.classList.add("active");
        } else {
            domWslMask.classList.add("animate");
            domMaskify.classList.add("animate");
            domRoot.classList.add("hide");
            domMaskify.classList.remove("hide");

            domNames.classList.remove("active");
            domNames.classList.add("hide");
        }
    }

    function DynamicHeaderText() {
        if (!show) {
            refDots?.current?.shown && (refDots.current.shown = false);
            return;
        }

        refDots.current.shown = true;

        let textIndex = -1;
        let text;

        refDots.current.classList.remove("hide");

        if (refDots.current._timeout) {
            clearTimeout(refDots.current._timeout);
            refDots.current._timeout = null;
        }

        function next() {
            if (!refDots?.current) {
                return;
            }

            if (textIndex === -1) {
                textIndex = 0;
                text = HeaderList[textIndex];
                refText.current.textContent = text;
            } else {
                textIndex++;
                text = HeaderList[textIndex];

                if (!HeaderList[textIndex + 1]) {
                    // refDots.current.classList.add("hide");
                    _onDesigningEnd();
                }

                if (text && refText.current) {
                    refText.current.textContent = text;
                }
            }

            if (HeaderList[textIndex + 1]) {
                if (generatePromises?.[textIndex] && !HeaderList[textIndex + 2]) {
                    generatePromises[textIndex].then(() => {
                        refDots.current._timeout = setTimeout(next, rand(HEADER_TIME.min, HEADER_TIME.max));
                    });
                } else {
                    refDots.current._timeout = setTimeout(next, rand(HEADER_TIME.min, HEADER_TIME.max));
                }
            } else {
                clearTimeout(refDots.current._timeout);
                refDots.current._timeout = null;
            }
        }

        next();
    }

    function DynamicHeaderDots() {
        if (!show) {
            return;
        }

        const domSpanList = Object.values(refDots.current.querySelectorAll("span"));
        const spanCount = domSpanList.length;

        let currentDotIndex = spanCount - 1;
        function next() {
            for (const domDot of domSpanList) {
                domDot.classList.remove("show");
            }

            const nextDomDot = domSpanList[currentDotIndex + 1] ?? domSpanList[0];
            currentDotIndex = domSpanList.indexOf(nextDomDot);
            nextDomDot.classList.add("show");
        }

        next();
        const intervalId = setInterval(next, 250);

        return () => {
            clearInterval(intervalId);
        };
    }

    function _onDesigningEnd() {
        setTimeout(() => onDesigningEnd(), ON_DESIGNING_END_TIMEOUT);
    }

    function onDomStepClick() {
        const domStep = this;

        const stepNum = parseInt(domStep.className.baseVal.match(/step-(\d+)/, "$1")[1]);

        const rootOff = domSvgBlocks.querySelector(".wsl-root").classList.contains("off");
        const stepOff = domStep.classList.contains("off");

        if (!rootOff && !stepOff) {
            setStepByIndex(stepNum);
        }
    }

    const CLRoot = ["wizard-designing"];
    step !== null && CLRoot.push("step");
    show && CLRoot.push("show");
    designing && CLRoot.push("designing");

    return (
        <div ref={refRoot} className={CLRoot.join(" ")}>
            <div className="wizard-designing-texts">
                <h1>
                    <span ref={refText}>{HeaderList[0]}</span>

                    <span ref={refDots} className="wizard-designing-dots">
                        <span>.</span>
                        <span>.</span>
                        <span>.</span>
                    </span>
                </h1>

                <div className="margin-top-40">
                    <p>
                        {t("WizardDesigningLayoutText1")}
                        <br />
                        {t("WizardDesigningLayoutText2")}
                    </p>
                </div>
            </div>

            <div className="margin-top-40 flex-center">
                <div ref={refWizardStepList} className="wizard-step-list">
                    <div className="wizard-step-list-img">
                        <img ref={refWizardStepListBlocks} src="/img/wizard-step-list.svg" />
                    </div>

                    <div className="wizard-step-list-img wizard-step-names">
                        <img ref={refWizardStepListNames} src="/img/wizard-step-list-names.svg" />
                    </div>
                </div>
            </div>

            {logotype && (
                <div>
                    <WizardLogotype show={show} fadeUp />
                </div>
            )}
        </div>
    );
}
